import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp, FaEdit, FaTrash } from "react-icons/fa";
import MDSpinner from "react-md-spinner";
import { toast } from "react-toastify";

import {
  fetchGroupFarms,
  fetchGroupLandPlots,
} from "../PendingSchedules/serviceOrderLocationApi";
import {
  deletePluviometerStation,
  fetchPluviometerRainSheet,
  fetchPluviometryMap,
} from "../../utils/farmPluviometryApi";
import FarmRainHeatmapMap from "./FarmRainHeatmapMap";
import FarmMapDeleteConfirmModal from "./FarmMapDeleteConfirmModal";
import PluviometerEditModal from "./PluviometerEditModal";
import PluviometerCreateModal from "./PluviometerCreateModal";
import PluviometerDayEditModal from "./PluviometerDayEditModal";
import PluviometerRainSheet from "./PluviometerRainSheet";
import RainMonthChart from "./RainMonthChart";
import RainMonthSheetModal from "./RainMonthSheetModal";
import RainYearChart from "./RainYearChart";
import { clampSize, useDragResize } from "./useDragResize";
import {
  applyDailyWindow,
  averageGaugeRainMm,
  buildDailyRainSeries,
  buildExampleDailyRainSeries,
  buildExampleMonthlyRainSeries,
  buildGaugesFromStations,
  buildMonthlyRainSeries,
  clampRainWindow,
  computeRainMapCenter,
  flattenBoundaryRings,
  findLastRainDate,
  findLastRainDateInRange,
  rainDaysInRange,
  flattenSheetCells,
  formatDateInputBr,
  formatRainMm,
  gaugesInsideRing,
  lastTwelveMonths,
  listSubFarmBoundaries,
  monthAllDays,
  monthDaysUntilToday,
  parseDateInputLocal,
  resolvePrimaryBoundaryRing,
  resolveQuickRainDate,
  resolveSubFarmFromViewport,
  seriesHasRain,
  toDateInputValue,
  yearsForDateRange,
} from "../../utils/rainHeatmapUtils";
import {
  MAP_VIEWPORT_SCOPES,
  readLocalMapLayout,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  writeLocalMapLayout,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import MapViewportToolbar from "../../components/MapViewportToolbar";

import "./FarmMapPluviometry.css";

const DEFAULT_CONTROLS_WIDTH = 320;
const DEFAULT_MAP_HEIGHT = 560;

function readRainMapLayout(farmId) {
  const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
  const maxMapHeight = Math.min(920, window.innerHeight * 0.78);
  const saved = readLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId);

  const savedControlsWidth =
    saved && saved.listWidth != null ? saved.listWidth : DEFAULT_CONTROLS_WIDTH;
  const savedMapHeight =
    saved && saved.mapHeight != null ? saved.mapHeight : DEFAULT_MAP_HEIGHT;

  return {
    controlsWidth: clampSize(savedControlsWidth, 260, maxControlsWidth),
    mapHeight: clampSize(savedMapHeight, 360, maxMapHeight),
  };
}

function currentMonthId() {
  return toDateInputValue().slice(0, 7);
}

function FarmMapPluviometry({ farmId }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [targetFarmName, setTargetFarmName] = useState("");
  const [boundaryRing, setBoundaryRing] = useState([]);
  const [subFarms, setSubFarms] = useState([]);
  const [landPlots, setLandPlots] = useState([]);
  const [gauges, setGauges] = useState([]);
  const [dailyByStation, setDailyByStation] = useState({});
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState(() => toDateInputValue());
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue());
  const [windowStart, setWindowStart] = useState(() => toDateInputValue());
  const [windowEnd, setWindowEnd] = useState(() => toDateInputValue());
  const [loadError, setLoadError] = useState("");
  const [boundaryHint, setBoundaryHint] = useState("");
  const [sheetGauge, setSheetGauge] = useState(null);
  const [editGauge, setEditGauge] = useState(null);
  const [deleteGauge, setDeleteGauge] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [quickRain, setQuickRain] = useState(null);
  const [placingMode, setPlacingMode] = useState(false);
  const [draftPoint, setDraftPoint] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [savedViewport, setSavedViewport] = useState(() =>
    readLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId)
  );
  const [mapReady, setMapReady] = useState(false);
  const [selectedSubFarmId, setSelectedSubFarmId] = useState("");
  const [focusToken, setFocusToken] = useState(0);
  const [showMonthSheet, setShowMonthSheet] = useState(false);
  const [gaugeListExpanded, setGaugeListExpanded] = useState(false);
  const [selectedMonthId, setSelectedMonthId] = useState(() => currentMonthId());
  const monthSheetRef = useRef(null);
  const windowStartRef = useRef(windowStart);
  const windowEndRef = useRef(windowEnd);
  windowStartRef.current = windowStart;
  windowEndRef.current = windowEnd;
  const mapApiRef = useRef(null);
  const skipNextAutoSubFarmRef = useRef(false);
  const [controlsWidth, setControlsWidth] = useState(
    () => readRainMapLayout(farmId).controlsWidth
  );
  const [mapHeight, setMapHeight] = useState(
    () => readRainMapLayout(farmId).mapHeight
  );
  const controlsWidthRef = useRef(controlsWidth);
  const mapHeightRef = useRef(mapHeight);

  useEffect(() => {
    controlsWidthRef.current = controlsWidth;
  }, [controlsWidth]);

  useEffect(() => {
    mapHeightRef.current = mapHeight;
  }, [mapHeight]);

  useEffect(() => {
    const layout = readRainMapLayout(farmId);
    controlsWidthRef.current = layout.controlsWidth;
    mapHeightRef.current = layout.mapHeight;
    setControlsWidth(layout.controlsWidth);
    setMapHeight(layout.mapHeight);
    setSavedViewport(readLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId));
    setMapReady(false);
    mapApiRef.current = null;
    setSelectedSubFarmId("");
    setFocusToken(0);
    setSelectedMonthId(currentMonthId());
    setGaugeListExpanded(false);
    skipNextAutoSubFarmRef.current = false;
  }, [farmId]);

  const handleLayoutResize = useCallback((axis, dx, dy) => {
    const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
    const maxMapHeight = Math.min(920, window.innerHeight * 0.78);

    if (axis === "controls") {
      setControlsWidth((current) => {
        const next = clampSize(current + dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-e") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-s" || axis === "map-se") {
      setMapHeight((current) => {
        const next = clampSize(current + dy, 360, maxMapHeight);
        mapHeightRef.current = next;
        return next;
      });
    }
    if (axis === "map-se") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
  }, []);

  function handleMapReady(map) {
    mapApiRef.current = map;
    setMapReady(true);
  }

  function handleSaveMapScreen() {
    if (!mapApiRef.current) {
      toast.error(t("Mapa ainda não está pronto."));
      return;
    }

    const viewport = readViewportFromGoogleMap(mapApiRef.current);
    if (!viewport) {
      toast.error(t("Não foi possível ler a posição atual do mapa."));
      return;
    }

    writeLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, viewport);
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, {
      listWidth: controlsWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
    setSavedViewport(viewport);
    toast.success(t("Tela do mapa salva neste navegador (posição e zoom)."));
  }

  const persistRainMapLayout = useCallback(() => {
    if (!farmId) return;
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, {
      listWidth: controlsWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
  }, [farmId]);

  const { onResizeStart } = useDragResize(handleLayoutResize, persistRainMapLayout);

  const periodLabel = useMemo(() => {
    if (windowStart && windowEnd) {
      if (windowStart === windowEnd) return formatDateInputBr(windowStart);
      return `${formatDateInputBr(windowStart)} – ${formatDateInputBr(windowEnd)}`;
    }
    return t("Período");
  }, [windowStart, windowEnd, t]);

  const quickRainDate = useMemo(() => {
    if (windowStart && windowEnd && windowStart === windowEnd) {
      return windowStart;
    }
    return resolveQuickRainDate(period, customStart, customEnd);
  }, [windowStart, windowEnd, period, customStart, customEnd]);

  const loadLiveContext = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      setLoadError("");
      try {
        const [groupFarms, plots, mapData] = await Promise.all([
          fetchGroupFarms(farmId),
          fetchGroupLandPlots(farmId),
          fetchPluviometryMap(farmId, "today", {
            start: customStart,
            end: customEnd,
          }),
        ]);

        const subFarmBoundaries = listSubFarmBoundaries(groupFarms);
        const ring = subFarmBoundaries.length
          ? []
          : resolvePrimaryBoundaryRing(groupFarms, farmId);
        const nextGauges = buildGaugesFromStations(mapData.stations || []);
        const parentFarm = (groupFarms || []).find(
          (farm) => String(farm.id) === String(farmId)
        );
        const yearMonths = lastTwelveMonths();
        const years = yearsForDateRange(
          yearMonths[0].start,
          yearMonths[yearMonths.length - 1].end
        );
        const daily = {};
        await Promise.all(
          nextGauges.map(async (gauge) => {
            if (!gauge.stationUuid) return;
            const cells = {};
            let loadedSheet = false;
            await Promise.all(
              years.map(async (year) => {
                try {
                  const sheet = await fetchPluviometerRainSheet(
                    farmId,
                    gauge.stationUuid,
                    year
                  );
                  Object.assign(cells, flattenSheetCells(sheet));
                  loadedSheet = true;
                } catch (error) {
                  return;
                }
              })
            );
            if (loadedSheet) {
              daily[gauge.stationUuid] = cells;
            }
          })
        );

        setTargetFarmName(
          (parentFarm && parentFarm.nome_da_fazenda) || t("Fazenda")
        );
        setSubFarms(subFarmBoundaries);
        setBoundaryRing(ring);
        setBoundaryHint(
          subFarmBoundaries.length === 0 && ring.length < 3
            ? t(
                "Nenhuma subfazenda com perímetro neste grupo. Desenhe as fazendas no mapa da fazenda para o calor recortar cada uma."
              )
            : ""
        );
        setLandPlots(plots);
        setGauges(nextGauges);
        setDailyByStation(daily);
        setSheetGauge((current) => {
          if (!current) return null;
          const updated = nextGauges.find((gauge) => gauge.id === current.id);
          return updated ? { ...current, ...updated } : current;
        });
      } catch (error) {
        setLoadError(t("Não foi possível carregar os pluviômetros da fazenda."));
        setGauges([]);
        setDailyByStation({});
        setSubFarms([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [farmId, t]
  );

  useEffect(() => {
    loadLiveContext();
  }, [loadLiveContext]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      loadLiveContext({ showSpinner: false });
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("visibilitychange", refresh);
    };
  }, [loadLiveContext]);

  const heatmapGauges = useMemo(
    () => applyDailyWindow(gauges, dailyByStation, windowStart, windowEnd),
    [gauges, dailyByStation, windowStart, windowEnd]
  );

  const selectedSubFarm = useMemo(
    () =>
      subFarms.find(
        (farm) => String(farm.id) === String(selectedSubFarmId)
      ) || null,
    [subFarms, selectedSubFarmId]
  );

  const scopedGauges = useMemo(() => {
    if (!selectedSubFarm) return heatmapGauges;
    return gaugesInsideRing(heatmapGauges, selectedSubFarm.ring);
  }, [heatmapGauges, selectedSubFarm]);

  const mapGauges = useMemo(
    () =>
      heatmapGauges.map((gauge) => ({
        ...gauge,
        lastRainDate: findLastRainDate(
          dailyByStation[gauge.stationUuid || gauge.id]
        ),
      })),
    [heatmapGauges, dailyByStation]
  );

  const gaugesWithLastRain = useMemo(
    () =>
      scopedGauges.map((gauge) => ({
        ...gauge,
        lastRainDate: findLastRainDate(
          dailyByStation[gauge.stationUuid || gauge.id]
        ),
      })),
    [scopedGauges, dailyByStation]
  );

  const visibleGauges = useMemo(() => {
    if (gaugeListExpanded || gaugesWithLastRain.length <= 1) {
      return gaugesWithLastRain;
    }
    if (sheetGauge) {
      const selected = gaugesWithLastRain.find(
        (gauge) => gauge.id === sheetGauge.id
      );
      if (selected) return [selected];
    }
    return gaugesWithLastRain.slice(0, 1);
  }, [gaugeListExpanded, gaugesWithLastRain, sheetGauge]);

  const yearMonths = useMemo(() => lastTwelveMonths(), []);

  const monthSeriesGauges = useMemo(() => {
    if (!selectedSubFarm) return gauges;
    return gaugesInsideRing(gauges, selectedSubFarm.ring);
  }, [gauges, selectedSubFarm]);

  const yearSeriesRaw = useMemo(
    () => buildMonthlyRainSeries(yearMonths, monthSeriesGauges, dailyByStation),
    [yearMonths, monthSeriesGauges, dailyByStation]
  );
  const yearIsExample = !seriesHasRain(yearSeriesRaw);
  const yearSeries = useMemo(
    () =>
      yearIsExample
        ? buildExampleMonthlyRainSeries(yearMonths)
        : yearSeriesRaw,
    [yearIsExample, yearMonths, yearSeriesRaw]
  );

  const chartDays = useMemo(
    () => monthDaysUntilToday(selectedMonthId),
    [selectedMonthId]
  );
  const printMonthDays = useMemo(
    () => monthAllDays(selectedMonthId),
    [selectedMonthId]
  );
  const selectedMonthMeta = useMemo(
    () => yearMonths.find((item) => item.id === selectedMonthId) || yearMonths[yearMonths.length - 1],
    [yearMonths, selectedMonthId]
  );

  const monthSeries = useMemo(
    () => buildDailyRainSeries(chartDays, monthSeriesGauges, dailyByStation),
    [chartDays, monthSeriesGauges, dailyByStation]
  );
  const chartIsExample = !seriesHasRain(monthSeries);
  const chartSeries = useMemo(
    () =>
      chartIsExample ? buildExampleDailyRainSeries(chartDays) : monthSeries,
    [chartIsExample, chartDays, monthSeries]
  );

  const monthTitle = useMemo(() => {
    const date = selectedMonthMeta
      ? parseDateInputLocal(selectedMonthMeta.start)
      : null;
    if (!date) return selectedMonthId;
    const locale = i18n.language === "en-US" ? "en-US" : "pt-BR";
    const label = date.toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [selectedMonthMeta, selectedMonthId, i18n.language]);

  function handleYearMonthClick(month) {
    if (!month) return;
    setSelectedMonthId(month.id);
    setWindowStart(month.start);
    setWindowEnd(month.end);
  }

  function handleChartDayClick(day) {
    if (!day || !selectedMonthMeta) return;
    const next = clampRainWindow(
      day,
      day,
      selectedMonthMeta.start,
      selectedMonthMeta.end
    );
    setWindowStart(next.start);
    setWindowEnd(next.end);
  }

  function handleChartWindowChange(start, end) {
    if (!start || !end || !selectedMonthMeta) return;
    const next = clampRainWindow(
      start,
      end,
      selectedMonthMeta.start,
      selectedMonthMeta.end
    );
    setWindowStart(next.start);
    setWindowEnd(next.end);
  }

  function handleCustomStart(value) {
    setCustomStart(value);
    setPeriod("custom");
    const end = customEnd && customEnd < value ? value : customEnd;
    setWindowStart(value);
    if (end) {
      setWindowEnd(end);
      setSelectedMonthId(end.slice(0, 7));
    }
  }

  function handleCustomEnd(value) {
    setCustomEnd(value);
    setPeriod("custom");
    const start = customStart && customStart > value ? value : customStart;
    setWindowEnd(value);
    if (start) setWindowStart(start);
    setSelectedMonthId(value.slice(0, 7));
  }

  const focusRing = useMemo(() => {
    if (selectedSubFarm) return selectedSubFarm.ring;
    return flattenBoundaryRings(subFarms.map((farm) => farm.ring));
  }, [selectedSubFarm, subFarms]);

  const handleSelectSubFarm = useCallback((id) => {
    skipNextAutoSubFarmRef.current = true;
    setSelectedSubFarmId(id == null ? "" : String(id));
    setFocusToken((current) => current + 1);
  }, []);

  const handleViewportIdle = useCallback(
    (viewport) => {
      if (!subFarms.length) return;
      if (skipNextAutoSubFarmRef.current) {
        skipNextAutoSubFarmRef.current = false;
        return;
      }
      const nextId = resolveSubFarmFromViewport(viewport, subFarms) || "";
      setSelectedSubFarmId((current) =>
        current === nextId ? current : nextId
      );
    },
    [subFarms]
  );

  const mapViewport = useMemo(() => {
    const rings = subFarms.map((farm) => farm.ring);
    const fitRing = flattenBoundaryRings(rings.length ? rings : [boundaryRing]);
    return computeRainMapCenter(fitRing, heatmapGauges);
  }, [subFarms, boundaryRing, heatmapGauges]);

  function handleStartPlacing() {
    setShowCreateModal(false);
    setPlacingMode(true);
    setDraftPoint(null);
    toast.info(t("Clique no mapa, em cima do talhão, para marcar o pluviômetro."));
  }

  const handleMapClick = useCallback(
    ({ lat, lng }) => {
      if (!placingMode) return;
      setDraftPoint({ lat, lng });
      setShowCreateModal(true);
    },
    [placingMode]
  );

  function handleCancelCreate() {
    setShowCreateModal(false);
    setPlacingMode(false);
    setDraftPoint(null);
  }

  function handleStationCreated() {
    setShowCreateModal(false);
    setPlacingMode(false);
    setDraftPoint(null);
    toast.success(t("Pluviômetro cadastrado."));
    loadLiveContext({ showSpinner: false });
  }

  function handleSelectGauge(gauge) {
    if (placingMode) return;
    setSheetGauge(gauge);
    setGaugeListExpanded(false);
  }

  function handleEditRain(gauge) {
    const daily = dailyByStation[gauge.stationUuid || gauge.id];
    const lastRainInWindow = findLastRainDateInRange(
      daily,
      windowStart,
      windowEnd
    );
    const dateStr =
      quickRainDate ||
      lastRainInWindow ||
      customEnd ||
      toDateInputValue();
    setQuickRain({
      gauge,
      dateStr,
      initialMm: gauge.mm,
      allowDateChange: !quickRainDate,
      replaceDays: quickRainDate
        ? []
        : rainDaysInRange(daily, windowStart, windowEnd),
    });
  }

  function handleGaugeSaved(updatedGauge) {
    setGauges((current) =>
      current.map((gauge) =>
        gauge.id === updatedGauge.id ? updatedGauge : gauge
      )
    );
    setSheetGauge((current) =>
      current && current.id === updatedGauge.id ? updatedGauge : current
    );
    loadLiveContext({ showSpinner: false });
  }

  function handleDailySaved() {
    loadLiveContext({ showSpinner: false });
  }

  function handleQuickRainSaved() {
    setQuickRain(null);
    toast.success(t("Chuva atualizada."));
    loadLiveContext({ showSpinner: false });
  }

  async function handleConfirmDelete() {
    if (!deleteGauge || !deleteGauge.stationUuid) return;
    setDeleting(true);
    try {
      await deletePluviometerStation(farmId, deleteGauge.stationUuid);
      setGauges((current) =>
        current.filter((gauge) => gauge.id !== deleteGauge.id)
      );
      setSheetGauge((current) =>
        current && current.id === deleteGauge.id ? null : current
      );
      setEditGauge((current) =>
        current && current.id === deleteGauge.id ? null : current
      );
      setDeleteGauge(null);
      toast.success(t("Pluviômetro excluído."));
    } catch (error) {
      toast.error(t("Não foi possível excluir o pluviômetro."));
    } finally {
      setDeleting(false);
    }
  }

  function handleGaugeDeleted(deletedGauge) {
    setGauges((current) =>
      current.filter((gauge) => gauge.id !== deletedGauge.id)
    );
    setSheetGauge((current) =>
      current && current.id === deletedGauge.id ? null : current
    );
    setEditGauge(null);
    toast.success(t("Pluviômetro excluído."));
  }

  const averageMm = averageGaugeRainMm(scopedGauges);
  const displayFarmName = selectedSubFarm
    ? selectedSubFarm.name || t("Subfazenda")
    : subFarms.length
    ? t("Todas as subfazendas")
    : targetFarmName;

  return (
    <div className="farm-map-pluviometry">
      <div className="farm-map-pluviometry__intro">
        <div>
          <h2>{t("Mapa de chuva da fazenda")}</h2>
          <p>
            {t(
              "O mapa de calor recorta cada subfazenda desenhada no mapa da fazenda. Marque os pluviômetros, lance os mm e use a régua de dias para ver a cor mudar."
            )}
          </p>
        </div>
      </div>

      <RainYearChart
        series={yearSeries}
        selectedMonthId={selectedMonthId}
        isExample={yearIsExample}
        onMonthClick={handleYearMonthClick}
      />
      <RainMonthChart
        monthId={selectedMonthId}
        monthTitle={monthTitle}
        series={chartSeries}
        isExample={chartIsExample}
        windowStart={windowStart}
        windowEnd={windowEnd}
        onDayClick={handleChartDayClick}
        onWindowChange={handleChartWindowChange}
      />

      {loading ? (
        <div className="farm-map-pluviometry__loading">
          <MDSpinner singleColor="#1ABB9C" size={36} />
        </div>
      ) : (
        <>
        <div
          className="farm-map-pluviometry__layout"
          style={{ "--controls-width": `${controlsWidth}px` }}
        >
          <aside className="farm-map-pluviometry__controls">
            <div className="farm-map-pluviometry__periods">
              <button
                type="button"
                className={
                  placingMode ? "btn btn-primary btn-sm" : "btn btn-default btn-sm"
                }
                onClick={() => {
                  if (placingMode) {
                    handleCancelCreate();
                    return;
                  }
                  handleStartPlacing();
                }}
              >
                {placingMode ? t("Cancelar marcação") : t("Adicionar pluviômetro no mapa")}
              </button>
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={() => {
                  setShowMonthSheet(true);
                  window.setTimeout(() => {
                    if (
                      monthSheetRef.current &&
                      monthSheetRef.current.scrollIntoView
                    ) {
                      monthSheetRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }, 50);
                }}
              >
                {t("Planilha do mês")}
              </button>
            </div>
            <div className="farm-map-pluviometry__summary">
              {subFarms.length ? (
                <label className="farm-map-pluviometry__summary-select">
                  <span>{t("Subfazenda")}</span>
                  <select
                    value={selectedSubFarmId}
                    onChange={(event) => handleSelectSubFarm(event.target.value)}
                  >
                    <option value="">{t("Todas as subfazendas")}</option>
                    {subFarms.map((farm) => (
                      <option key={farm.id} value={String(farm.id)}>
                        {farm.name || t("Subfazenda")}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div>
                  <span>{t("Fazenda")}</span>
                  <strong>{displayFarmName}</strong>
                </div>
              )}
              <div>
                <span>{t("Período")}</span>
                <strong>{periodLabel}</strong>
              </div>
              <div>
                <span>{t("Média")}</span>
                <strong>{formatRainMm(averageMm)} mm</strong>
              </div>
            </div>

            <p className="farm-map-pluviometry__hint">
              {placingMode
                ? t("Clique no mapa para posicionar o pluviômetro. O ponto entra na coordenada do clique.")
                : t(
                    "A lista mostra um pluviômetro por vez. Clique no mapa para abrir só aquele e ver a data da última chuva, ou expanda os nomes."
                  )}
            </p>
            {boundaryHint ? (
              <p className="farm-map-pluviometry__error">{boundaryHint}</p>
            ) : null}
            {loadError ? (
              <p className="farm-map-pluviometry__error">{loadError}</p>
            ) : null}
            {!gauges.length && !loadError ? (
              <p className="farm-map-pluviometry__hint">
                {t("Nenhum pluviômetro nesta fazenda ainda. Use Adicionar pluviômetro no mapa.")}
              </p>
            ) : null}
            {gauges.length && selectedSubFarm && !scopedGauges.length ? (
              <p className="farm-map-pluviometry__hint">
                {t("Nenhum pluviômetro nesta subfazenda.")}
              </p>
            ) : null}

            {gaugesWithLastRain.length ? (
              <div className="farm-map-pluviometry__gauge-head">
                <strong>
                  {t("Pluviômetros")}
                  {gaugesWithLastRain.length > 1
                    ? ` (${
                        gaugeListExpanded
                          ? gaugesWithLastRain.length
                          : `1/${gaugesWithLastRain.length}`
                      })`
                    : ""}
                </strong>
                {gaugesWithLastRain.length > 1 ? (
                  <button
                    type="button"
                    className="farm-map-pluviometry__gauge-toggle"
                    onClick={() => setGaugeListExpanded((current) => !current)}
                    aria-expanded={gaugeListExpanded}
                    title={
                      gaugeListExpanded
                        ? t("Mostrar um pluviômetro")
                        : t("Mostrar todos os pluviômetros")
                    }
                    aria-label={
                      gaugeListExpanded
                        ? t("Mostrar um pluviômetro")
                        : t("Mostrar todos os pluviômetros")
                    }
                  >
                    {gaugeListExpanded ? (
                      <FaChevronUp aria-hidden />
                    ) : (
                      <FaChevronDown aria-hidden />
                    )}
                    <span>
                      {gaugeListExpanded
                        ? t("Minimizar")
                        : t("Mostrar todos")}
                    </span>
                  </button>
                ) : null}
              </div>
            ) : null}

            <ul className="farm-map-pluviometry__gauge-list">
              {visibleGauges.map((gauge) => (
                <li key={gauge.id} className="farm-map-pluviometry__gauge-item">
                  <button
                    type="button"
                    className={
                      sheetGauge && sheetGauge.id === gauge.id
                        ? "farm-map-pluviometry__gauge-button farm-map-pluviometry__gauge-button--active"
                        : "farm-map-pluviometry__gauge-button"
                    }
                    onClick={() => handleSelectGauge(gauge)}
                  >
                    <span>{gauge.label}</span>
                    <strong>{formatRainMm(gauge.mm)} mm</strong>
                    <small>{periodLabel}</small>
                    <small className="farm-map-pluviometry__last-rain">
                      {gauge.lastRainDate
                        ? t("Última chuva: {{date}}", {
                            date: formatDateInputBr(gauge.lastRainDate),
                          })
                        : t("Sem chuva registrada")}
                    </small>
                  </button>
                  <div className="farm-map-pluviometry__gauge-actions">
                    <button
                      type="button"
                      className="farm-map-pluviometry__gauge-icon-btn"
                      title={t("Editar chuva (mm)")}
                      aria-label={t("Editar chuva (mm)")}
                      onClick={() => handleEditRain(gauge)}
                    >
                      <FaEdit aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="farm-map-pluviometry__gauge-icon-btn farm-map-pluviometry__gauge-icon-btn--danger"
                      title={t("Excluir pluviômetro")}
                      aria-label={t("Excluir pluviômetro")}
                      onClick={() => setDeleteGauge(gauge)}
                    >
                      <FaTrash aria-hidden />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <div
            className="farm-map-pluviometry__layout-resize"
            title={t("Arraste para redimensionar o mapa")}
            onMouseDown={(event) => onResizeStart("controls", event)}
          />

          <div className="farm-map-pluviometry__map-column">
          <div
            className="farm-map-pluviometry__map-wrap"
            style={{ height: mapHeight }}
          >
            <FarmRainHeatmapMap
              boundaryRing={boundaryRing}
              subFarms={subFarms}
              landPlots={landPlots}
              gauges={mapGauges}
              center={
                savedViewport
                  ? { lat: savedViewport.lat, lng: savedViewport.lng }
                  : mapViewport.center
              }
              zoom={savedViewport ? savedViewport.zoom : mapViewport.zoom}
              savedViewport={savedViewport}
              mapHeight={mapHeight}
              selectedGaugeId={sheetGauge ? sheetGauge.id : null}
              selectedSubFarmId={selectedSubFarmId}
              focusRing={focusRing}
              focusToken={focusToken}
              onGaugeClick={handleSelectGauge}
              onMapReady={handleMapReady}
              onViewportIdle={handleViewportIdle}
              onSubFarmClick={handleSelectSubFarm}
              placingMode={placingMode}
              draftPoint={draftPoint}
              onMapClick={handleMapClick}
            />
            {placingMode ? (
              <div className="farm-map-pluviometry__placing-hint">
                {t("Clique no talhão para marcar o pluviômetro.")}
              </div>
            ) : null}
            <MapViewportToolbar
              onSave={handleSaveMapScreen}
              disabled={!mapReady}
              label={t("Salvar tela")}
              savingLabel={t("Salvando...")}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--e"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-e", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--s"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-s", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--se"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-se", event)}
            />
          </div>
          <div className="farm-map-pluviometry__custom-below">
            <strong>{t("Período personalizado")}</strong>
            <p>
              {t(
                "Escolha um intervalo de datas para o mapa. Sem dias futuros."
              )}
            </p>
            <div className="farm-map-pluviometry__custom-dates">
              <label>
                {t("De")}
                <input
                  type="date"
                  value={customStart}
                  max={toDateInputValue()}
                  onChange={(event) => handleCustomStart(event.target.value)}
                />
              </label>
              <label>
                {t("Até")}
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  max={toDateInputValue()}
                  onChange={(event) => handleCustomEnd(event.target.value)}
                />
              </label>
            </div>
          </div>
          </div>
        </div>
      </>
      )}

      {sheetGauge ? (
        <PluviometerRainSheet
          farmId={farmId}
          gauge={{
            ...sheetGauge,
            lastRainDate: findLastRainDate(
              dailyByStation[sheetGauge.stationUuid || sheetGauge.id]
            ),
          }}
          onEditGauge={setEditGauge}
          onDailySaved={handleDailySaved}
        />
      ) : null}

      {editGauge ? (
        <PluviometerEditModal
          gauge={editGauge}
          farmId={farmId}
          onClose={() => setEditGauge(null)}
          onSaved={handleGaugeSaved}
          onDeleted={handleGaugeDeleted}
        />
      ) : null}

      {quickRain ? (
        <PluviometerDayEditModal
          farmId={farmId}
          stationUuid={quickRain.gauge.stationUuid}
          stationLabel={quickRain.gauge.label}
          dateStr={quickRain.dateStr}
          initialMm={quickRain.initialMm}
          allowDateChange={quickRain.allowDateChange}
          replaceDays={quickRain.replaceDays}
          onClose={() => setQuickRain(null)}
          onSaved={handleQuickRainSaved}
        />
      ) : null}

      {showCreateModal ? (
        <PluviometerCreateModal
          farmId={farmId}
          initialLatitude={draftPoint ? draftPoint.lat : ""}
          initialLongitude={draftPoint ? draftPoint.lng : ""}
          pickedFromMap={Boolean(draftPoint)}
          onClose={handleCancelCreate}
          onCreated={handleStationCreated}
        />
      ) : null}

      {showMonthSheet ? (
        <div ref={monthSheetRef}>
          <RainMonthSheetModal
            open
            onClose={() => setShowMonthSheet(false)}
            farmName={displayFarmName}
            monthTitle={monthTitle}
            days={printMonthDays}
            gauges={monthSeriesGauges}
            dailyByStation={dailyByStation}
          />
        </div>
      ) : null}

      <FarmMapDeleteConfirmModal
        open={Boolean(deleteGauge)}
        title={t("Excluir pluviômetro")}
        message={t(
          "Tem certeza de que deseja excluir o pluviômetro {{name}}? Ele deixará de aparecer no mapa e no histórico de chuva.",
          { name: (deleteGauge && deleteGauge.label) || t("Pluviômetro") }
        )}
        confirmLabel={t("Excluir pluviômetro")}
        cancelLabel={t("Cancelar")}
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setDeleteGauge(null);
        }}
      />
    </div>
  );
}

export default FarmMapPluviometry;
